/*
 * Consultation landing page (templates/page.consultation.json).
 * - FAQ accordion (sections/capucinne-consultation-faq.liquid): each question is a
 *   button with aria-expanded/aria-controls; its answer panel toggles `hidden`.
 * - Booking (sections/capucinne-consultation-booking.liquid): the inline calendar is
 *   built here (its container says data-auto-load="false" to Calendly's widget.js), so
 *   Calendly gets the booking UTMs of capucinne-calendly-tracking.js: utm_content =
 *   this page's path, utm_term = the panel's data-cap-booking-placement. The link under
 *   the calendar gets the same UTMs. Without window.CapCalendly the calendar and the
 *   link (server href) carry only utm_content and utm_term; without Calendly (late,
 *   blocked) the link still books. A section the
 *   theme editor re-renders is built again.
 *   Speed: Chrome pauses the rendering of an off-screen cross-origin frame, and
 *   Calendly's page only starts building (event type, available dates) once it renders,
 *   so a calendar further down the page used to start only when scrolled to, then take
 *   3-6 s. The frame is built at once and, while its slot is below the screen (or shows
 *   only the frame's clipped top), pinned to
 *   the viewport (position: fixed) at the slot's size, all but transparent, inert and
 *   hidden from assistive tech, so Calendly builds it while the shopper reads the hero;
 *   it goes back to its slot as soon as Calendly reports its page (or the slot scrolls
 *   into view). Until then the slot shows a calendar-shaped skeleton (aria-hidden) and a
 *   visually hidden status. Without widget.js (known when this script runs) a slot below
 *   the screen gives way to the link under it, and one already on or above the screen
 *   keeps its height and shows that link at its top, so nothing on the screen moves.
 * Loaded by both sections, so it guards against running twice.
 */
(function () {
  'use strict';

  if (window.CapConsultationPage) return;
  window.CapConsultationPage = true;

  function closest(node, className) {
    while (node && node.nodeType === 1) {
      if (node.classList && node.classList.contains(className)) return node;
      node = node.parentNode;
    }
    return null;
  }

  function setOpen(item, open) {
    var toggle = item.querySelector('.cap-consult-faq__toggle');
    var panelId = toggle ? toggle.getAttribute('aria-controls') : '';
    var panel = panelId ? document.getElementById(panelId) : null;
    if (!toggle || !panel) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    item.classList.toggle('is-open', open);
    if (open) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  }

  document.addEventListener('click', function (event) {
    var toggle = closest(event.target, 'cap-consult-faq__toggle');
    if (!toggle) return;
    var item = closest(toggle, 'cap-consult-faq__item');
    if (!item) return;
    setOpen(item, toggle.getAttribute('aria-expanded') !== 'true');
  });

  // Theme editor: selecting a question block opens it so the merchant sees the answer.
  document.addEventListener('shopify:block:select', function (event) {
    var item = event.target && event.target.hasAttribute && event.target.hasAttribute('data-cap-consult-faq-item') ? event.target : null;
    if (item) setOpen(item, true);
  });

  var PLACEMENT = 'data-cap-booking-placement';
  var DEFAULT_PLACEMENT = 'Consultation page calendar';
  // On the panel: loading (as rendered) -> ready (Calendly reported its page, or the
  // skeleton's time ran out) | unavailable (no widget.js: the slot gives way to the link;
  // with data-cap-booking-keep, a slot already on or above the screen keeps its height
  // and shows a copy of the link at its top instead).
  var STATE = 'data-cap-booking-state';
  var KEEP = 'data-cap-booking-keep';
  var SLOT_LINK = 'data-cap-booking-slot-link';
  var CALENDLY_ORIGIN = /^https:\/\/([a-z0-9-]+\.)*calendly\.com$/i;
  var WIDGET_JS = /calendly\.com\/assets\/external\/widget\.js/;
  // Calendly's loading page reports 26px, then 2px; its booking page 700px and more.
  var READY_HEIGHT = 300;
  // The hidden build goes back to its slot after this, even without word from Calendly
  // (on a slow phone, with the page's other scripts, Calendly can take 25-30 s).
  var WARM_MAX = 60000;
  // The skeleton gives way after the slot has been on screen this long without word
  // from Calendly (or this long plus 8 s after the build without IntersectionObserver),
  var SKELETON_MAX = 30000;
  // or this long after the frame's page loaded without one message from it (an error
  // or a stalled page; a working page, rendered, speaks within about 2 s of loading).
  var SILENT_MAX = 8000;
  // After Calendly's ready message, a moment more pinned on screen for its last renders.
  var DOCK_DELAY = 400;

  // capucinne-calendly-tracking.js (site-wide, deferred in <head>), when present.
  function bookingTracker() {
    var api = window.CapCalendly;
    return api && typeof api.utmFor === 'function' && typeof api.urlFor === 'function' ? api : null;
  }

  function calendlyReady() {
    return !!(window.Calendly && typeof window.Calendly.initInlineWidget === 'function');
  }

  // The link under the calendar (new tab) carries the calendar's UTMs. Its plain
  // URL goes in data-cap-calendly-href, where capucinne-calendly-tracking.js starts
  // from when it refreshes the UTMs on click.
  function tagLink(link, url, label, api) {
    if (!link || !url || !api || link.hasAttribute('data-cap-calendly-href')) return;
    try {
      var tagged = api.urlFor(url, label);
      if (!tagged || tagged === url) return;
      link.setAttribute('data-cap-calendly-href', url);
      link.setAttribute('href', tagged);
    } catch (error) {
      /* the plain link still opens Calendly */
    }
  }

  function panelOf(widget) {
    return closest(widget, 'cap-consult-booking__panel');
  }

  function stateOf(widget) {
    var panel = panelOf(widget);
    return panel ? panel.getAttribute(STATE) || '' : '';
  }

  function setState(widget, state) {
    var panel = panelOf(widget);
    if (!panel || panel.getAttribute(STATE) === state) return;
    panel.setAttribute(STATE, state);
    var status = panel.querySelector('[data-cap-booking-status]');
    if (status) {
      // Its text back if a late widget.js brings a slot that gave way back to loading.
      if (status.capBookingText == null) status.capBookingText = status.textContent;
      status.textContent = state === 'loading' ? status.capBookingText : '';
    }
    var skeleton = widget.querySelector('[data-cap-booking-skeleton]');
    if (!skeleton) return;
    if (state === 'loading') {
      skeleton.hidden = false;
      return;
    }
    // After its fade (the CSS transition): out of the rendering, its shimmer stops.
    setTimeout(function () {
      if (stateOf(widget) !== 'loading') skeleton.hidden = true;
    }, 600);
  }

  function viewportHeight() {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  function inView(node) {
    var rect = node.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < viewportHeight();
  }

  // Only a slot still below the viewport: widget.js scrolls the page back up to a
  // calendar whose height arrives while its top is above the viewport.
  function belowView(node) {
    return node.getBoundingClientRect().top >= viewportHeight();
  }

  // How much of the frame's top its clip hides in the slot (capucinne-consultation.css:
  // 40px where the frame is pulled up under the intro, 86px on the phone page while it
  // loads). A pinned frame has no clip: the value from when it was pinned.
  function clipTop(widget) {
    if (widget.capBookingWarm) return widget.capBookingWarm.clip;
    var frame = widget.querySelector('iframe');
    if (!frame) return 0;
    var match = /inset\(\s*([\d.]+)px/.exec(String(window.getComputedStyle(frame).clipPath || ''));
    return match ? parseFloat(match[1]) || 0 : 0;
  }

  // Nothing of the frame shows yet: its slot below the viewport, or only the strip
  // its clip hides on screen. A frame with nothing to paint is not rendered either,
  // so Calendly would not build it there (the calendar came about 9 s later).
  function hiddenBelow(widget) {
    return widget.getBoundingClientRect().top + clipTop(widget) >= viewportHeight();
  }

  // inert keeps the hidden frame out of focus and the accessibility tree.
  function canWarm(frame) {
    return 'inert' in frame && typeof window.IntersectionObserver === 'function';
  }

  function unwarm(frame) {
    frame.removeAttribute('data-cap-warming');
    frame.removeAttribute('aria-hidden');
    frame.removeAttribute('tabindex');
    frame.inert = false;
    frame.style.removeProperty('--cap-booking-warm-width');
    frame.style.removeProperty('--cap-booking-warm-height');
  }

  // The frame back in its slot: styles only, so its page is kept, not reloaded.
  function dock(widget) {
    var warm = widget.capBookingWarm;
    if (!warm) return;
    widget.capBookingWarm = null;
    clearTimeout(warm.timer);
    clearTimeout(warm.docking);
    window.removeEventListener('scroll', warm.check);
    window.removeEventListener('resize', warm.check);
    unwarm(warm.frame);
  }

  // While the slot is below the viewport (or only the strip the frame's clip hides is
  // on screen), the frame is pinned to the viewport at the
  // slot's size (iframe[data-cap-warming] in capucinne-consultation.css: opacity 0.01,
  // not 0, which Chrome draws at a much slower rate; no pointer events): the browser
  // renders it there, so Calendly builds its page. Inert and aria-hidden meanwhile. An
  // ancestor that would hold a fixed frame in place (transform, containment) leaves it
  // in its slot, as before.
  function warm(widget, frame) {
    if (!frame || widget.capBookingWarm || !canWarm(frame) || !hiddenBelow(widget)) return false;
    var rect = widget.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    // Read before pinning (a pinned frame has no clip).
    var clip = clipTop(widget);
    frame.style.setProperty('--cap-booking-warm-width', Math.round(rect.width) + 'px');
    frame.style.setProperty('--cap-booking-warm-height', Math.round(rect.height) + 'px');
    frame.setAttribute('data-cap-warming', '');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.inert = true;
    if (!inView(frame)) {
      unwarm(frame);
      return false;
    }
    // The slot no longer below the viewport, past the clipped strip (scrolled to, or
    // jumped past with a key or a link, which the IntersectionObserver below may never
    // see): back to the slot at once, so a height Calendly reports can never pull the
    // page back up. Still below after a resize: the pinned frame takes the slot's new
    // size.
    var check = function (event) {
      if (!widget.capBookingWarm) return;
      if (!hiddenBelow(widget)) {
        dock(widget);
        return;
      }
      if (!event || event.type !== 'resize') return;
      var box = widget.getBoundingClientRect();
      frame.style.setProperty('--cap-booking-warm-width', Math.round(box.width) + 'px');
      frame.style.setProperty('--cap-booking-warm-height', Math.round(box.height) + 'px');
    };
    widget.capBookingWarm = {
      frame: frame,
      timer: setTimeout(function () { dock(widget); }, WARM_MAX),
      docking: 0,
      check: check,
      clip: clip
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    return true;
  }

  function calendarReady(widget) {
    clearTimeout(widget.capBookingSkeletonTimer);
    setState(widget, 'ready');
    var warm = widget.capBookingWarm;
    if (warm && !warm.docking) {
      warm.docking = setTimeout(function () { dock(widget); }, DOCK_DELAY);
    }
  }

  // The skeleton's clock, from the slot's first time on screen (restart: from now):
  // SKELETON_MAX, or SILENT_MAX while the frame's page has loaded and not said a word.
  // Silence only counts while the frame renders (pinned or on screen): a frame left
  // off screen in its slot says nothing in Chrome, working or not.
  function skeletonClock(widget, restart) {
    if (stateOf(widget) !== 'loading' || !widget.capBookingSeen) return;
    if (widget.capBookingSkeletonTimer && !restart) return;
    clearTimeout(widget.capBookingSkeletonTimer);
    var silent = !!widget.capBookingLoaded && !widget.capBookingHeard && typeof window.IntersectionObserver === 'function';
    widget.capBookingSkeletonShort = silent;
    widget.capBookingSkeletonTimer = setTimeout(function () {
      widget.capBookingSkeletonTimer = 0;
      if (stateOf(widget) !== 'loading') return;
      if (silent && !widget.capBookingWarm && !inView(widget)) {
        skeletonClock(widget);
        return;
      }
      setState(widget, 'ready');
    }, silent ? SILENT_MAX : widget.capBookingSkeletonBase || SKELETON_MAX);
  }

  // Any message from our frame: Calendly's page is alive, so a short (silent) clock
  // goes back to the full one.
  function heard(widget) {
    if (widget.capBookingHeard) return;
    widget.capBookingHeard = true;
    if (widget.capBookingSkeletonShort && widget.capBookingSkeletonTimer) skeletonClock(widget, true);
  }

  function frameLoaded(widget) {
    widget.capBookingLoaded = true;
    if (!widget.capBookingHeard && widget.capBookingSkeletonTimer) skeletonClock(widget, true);
  }

  // Until Calendly reports: the slot on screen brings the frame back to it (Calendly
  // goes on building there) and starts the skeleton's clock; the slot below the
  // viewport again (scrolled back up) pins the frame again, so the build goes on. A
  // slot whose only part on screen is the strip the frame's clip hides keeps the frame
  // pinned (or pins it): in the slot it would have nothing to paint.
  function watch(widget) {
    if (typeof window.IntersectionObserver !== 'function') {
      widget.capBookingSeen = true;
      widget.capBookingSkeletonBase = SKELETON_MAX + 8000;
      skeletonClock(widget);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      if (stateOf(widget) !== 'loading' || !widget.isConnected) {
        observer.disconnect();
        return;
      }
      if (entries[entries.length - 1].isIntersecting) {
        if (hiddenBelow(widget)) {
          warm(widget, widget.querySelector('iframe'));
        } else {
          dock(widget);
        }
        widget.capBookingSeen = true;
        skeletonClock(widget);
      } else {
        warm(widget, widget.querySelector('iframe'));
      }
    });
    observer.observe(widget);
  }

  function widgetOfSource(source) {
    if (!source) return null;
    var frames = document.querySelectorAll('.cap-consult-booking__widget iframe');
    for (var i = 0; i < frames.length; i += 1) {
      if (frames[i].contentWindow === source) return closest(frames[i], 'cap-consult-booking__widget');
    }
    return null;
  }

  // Calendly's ready word, from our own frame only: its event type page shown, or a
  // real page height (not the 26px / 2px of its loading page).
  window.addEventListener('message', function (event) {
    if (!event || !CALENDLY_ORIGIN.test(String(event.origin || ''))) return;
    var data = event.data;
    if (!data || typeof data !== 'object') return;
    var widget = widgetOfSource(event.source);
    if (!widget) return;
    heard(widget);
    var height = data.payload && typeof data.payload === 'object' ? parseFloat(data.payload.height) : 0;
    if (data.event !== 'calendly.event_type_viewed' && !(data.event === 'calendly.page_height' && height >= READY_HEIGHT)) return;
    calendarReady(widget);
  });

  // No calendar for this slot (no widget.js, or it failed to build): a slot still
  // below the screen gives way to the link under it; one on or above the screen (#book,
  // or scrolled to or past) keeps its height, so nothing on the screen moves, and shows
  // a copy of that link at its top (the line under it keeps its place, hidden).
  function keepSlot(widget) {
    var panel = panelOf(widget);
    if (!panel || panel.hasAttribute(KEEP)) return;
    panel.setAttribute(KEEP, '');
    var line = panel.querySelector('.cap-consult-booking__fallback');
    if (!line || widget.querySelector('[' + SLOT_LINK + ']')) return;
    var copy = line.cloneNode(true);
    copy.setAttribute(SLOT_LINK, '');
    // Where the slot shows under the intro: the frame's pull-up under it, if any.
    var pull = -parseFloat(window.getComputedStyle(widget).marginTop);
    copy.style.top = (pull > 0 ? Math.round(pull) : 0) + 'px';
    widget.appendChild(copy);
  }

  function unkeepSlot(widget) {
    var panel = panelOf(widget);
    if (panel) panel.removeAttribute(KEEP);
    var copy = widget.querySelector('[' + SLOT_LINK + ']');
    if (copy) copy.parentNode.removeChild(copy);
  }

  function giveWay(widget) {
    if (stateOf(widget) === 'unavailable') return;
    if (!belowView(widget)) keepSlot(widget);
    setState(widget, 'unavailable');
  }

  function initCalendly(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var api = bookingTracker();
    var widgets = scope.querySelectorAll('.cap-consult-booking__widget');
    for (var i = 0; i < widgets.length; i += 1) {
      var widget = widgets[i];
      var url = widget.getAttribute('data-url');
      var panel = closest(widget, 'cap-consult-booking__panel');
      var label = (panel && panel.getAttribute(PLACEMENT)) || DEFAULT_PLACEMENT;
      tagLink(panel && panel.querySelector('[data-cap-consult-calendly-link]'), url, label, api);
      // Once per container: data-processed is also widget.js's own "built" flag.
      if (!url || widget.getAttribute('data-processed') || widget.querySelector('iframe')) continue;
      if (!calendlyReady()) continue;
      // inlineStyles / resize as widget.js's own build: position: relative on the
      // container, which then follows the calendar's height (min-height in the CSS).
      var options = { url: url, parentElement: widget, inlineStyles: true, resize: true };
      try {
        // Without the tracking script, at least the page and the placement (as the
        // no-JS link under the calendar carries them).
        options.utm = api ? api.utmFor(label) : { utmContent: String(window.location.pathname || '/').slice(0, 250), utmTerm: label };
      } catch (error) {
        /* no UTMs: the calendar still opens */
      }
      widget.setAttribute('data-processed', 'true');
      try {
        window.Calendly.initInlineWidget(options);
      } catch (error) {
        widget.removeAttribute('data-processed');
        giveWay(widget);
        continue;
      }
      if (stateOf(widget) === 'unavailable') {
        unkeepSlot(widget);
        setState(widget, 'loading');
      }
      var frame = widget.querySelector('iframe');
      if (frame) {
        frame.addEventListener('load', frameLoaded.bind(null, widget));
      }
      try {
        warm(widget, frame);
      } catch (error) {
        /* the frame loads in its slot, as before */
      }
      watch(widget);
    }
  }

  function initPage() {
    initCalendly(document);
  }

  // No widget.js (blocked, failed): the slots give way to the link (see giveWay).
  function markUnavailable() {
    if (calendlyReady()) return;
    var widgets = document.querySelectorAll('.cap-consult-booking__widget');
    for (var i = 0; i < widgets.length; i += 1) {
      if (!widgets[i].querySelector('iframe')) giveWay(widgets[i]);
    }
  }

  // widget.js still to come: an async or script-inserted copy. The theme's own one is
  // deferred in <head>, before this deferred script, so it has run or failed by now.
  function widgetScriptPending() {
    var scripts = document.getElementsByTagName('script');
    for (var i = 0; i < scripts.length; i += 1) {
      if (scripts[i].async && WIDGET_JS.test(scripts[i].src || '')) return true;
    }
    return false;
  }

  // This script is deferred and comes after widget.js and capucinne-calendly-tracking.js
  // (deferred in <head>), so the calendar is built as soon as it runs, before the
  // remaining deferred scripts and the DOMContentLoaded listeners, and a missing
  // widget.js is known right then (not only at the window's load, seconds later).
  // DOMContentLoaded and load stay as later chances, and a widget.js that arrives later
  // still gets its turn from its own load event (a slot that gave way comes back); the
  // guard above keeps it to one calendar.
  if (document.readyState !== 'loading') {
    initPage();
    if (!widgetScriptPending()) markUnavailable();
  }
  if (document.readyState === 'complete') {
    markUnavailable();
  } else {
    document.addEventListener('DOMContentLoaded', initPage);
    window.addEventListener('load', function () {
      initPage();
      markUnavailable();
    });
  }

  document.addEventListener('load', function (event) {
    var node = event.target;
    if (node && node.tagName === 'SCRIPT' && WIDGET_JS.test(node.src || '')) {
      initPage();
    }
  }, true);

  document.addEventListener('error', function (event) {
    var node = event.target;
    if (node && node.tagName === 'SCRIPT' && WIDGET_JS.test(node.src || '')) {
      markUnavailable();
    }
  }, true);

  // Theme editor: a re-rendered booking section gets a fresh, empty widget container.
  document.addEventListener('shopify:section:load', function (event) {
    initCalendly(event.target);
    if (!widgetScriptPending()) markUnavailable();
  });
})();
